import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SelectBox } from "devextreme-react/select-box";
import moment from "moment";
import { MasterPage } from "../../Shared/master";
import ResponsiveRecords from "../../Shared/ResponsiveRecords";
import { DataGridSkeletonPageSimpleWithTitle } from "../../skeleton/GridSkeleton.js";
import LiveTripTracking from "../../map/LiveTripTracking";
import TripReplayMap from "../../map/TripReplayMap";
import { axiosGet, axiosPost } from "../../../apis/useAxios";
import { showErrorToast } from "../../../helpers/message";
import { stashGridState, popGridState } from "../../../helpers/gridStateCache";
import { DUTY_TYPES, TILES, TIME_SLOTS, countClub, countTrips, filterTrips, tripSearchText } from "./dispatchRules";
import DispatchFilterBar from "./DispatchFilterBar";
import DispatchSummary from "./DispatchSummary";
import DispatchGrid from "./DispatchGrid";
import DispatchCard from "./DispatchCard";
import "./dispatch.css";

const today = () => moment().startOf("day").toDate();
const START = { from: today(), to: today(), airportCode: "" };
const TILE_IDS = TILES.map((t) => t.id);
const ALL_VIEW = { dutyTypeId: 0, timeSlotId: 0, club: false, tile: "" }; // the Rides tile: every loaded ride
const NO_TRACK ={ open: false, transactionId: "", airlineCode: "", employeeNumber: "", vehicleNo: "" };

/**
 * Dispatch (Crew Transfer): the airline's rides for a date range, airport and trip status (Rides/get-rides), with summary
 * tiles, duty type / time slot filters, a grid on desktop and cards on phones. Live tracking and trip replay open in the
 * slide-in panels (unchanged); the crew's mobile view opens /crew-activity-mobile and Back restores this page.
 */
export function DispatchComponent({ history }) {
  // Opened from the Dashboard: ?tile=pending|onJob|completed|cancelled and ?airport=CODE pre-select today's view.
  const query = new URLSearchParams(history?.location?.search ?? "");
  const firstFilters = useRef({ ...START, airportCode: query.get("airport") ?? "" });
  const [filters, setFilters] = useState(firstFilters.current);
  const [applied, setApplied] = useState(START); // the range the rides were loaded for (shown above the grid)
  const [airports, setAirports] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [view, setView] = useState(() => ({ dutyTypeId: 0, timeSlotId: 0, club: false, tile: TILE_IDS.includes(query.get("tile")) ? query.get("tile") : "" }));
  const [track, setTrack] = useState(NO_TRACK);
  const [replay, setReplay] = useState(null);
  const gridRef = useRef(null);

  const resizeGrid = () => setTimeout(() => gridRef.current?.instance?.updateDimensions(), 0);

  const loadTrips = useCallback(async (f) => {
    const from = moment(f.from ?? new Date()).startOf("day");
    const to = moment(f.to ?? f.from ?? new Date()).endOf("day"); // end stays empty until the second click
    const res = await axiosPost("Rides/get-rides", {
      AirlineCode: sessionStorage.getItem("session_airlineCode"),
      FromDate: from.format("YYYY-MM-DD HH:mm:ss"),
      ToDate: to.format("YYYY-MM-DD HH:mm:ss"),
      AirportCode: f.airportCode,
    });
    setTrips((res?.payload ?? []).filter((t) => t.tripStatusId !== 118));
    setApplied({ ...f, from: from.toDate(), to: to.toDate() });
  }, []);

  useEffect(() => {
    // Back from crew-activity-mobile restores the page as it was instead of loading again.
    const cached = popGridState();
    if (cached && cached.page === "dispatch") {
      setFilters(cached.filters);
      setApplied(cached.applied);
      setAirports(cached.airports);
      setTrips(cached.trips);
      setView(cached.view);
      setLoading(false);
      return;
    }
    const airline = sessionStorage.getItem("session_airlineCode");
    Promise.all([axiosGet(`Airport/get-airport-dropdown/${airline}`).then((r) => setAirports(r?.payload ?? [])), loadTrips(firstFilters.current)])
      .catch((err) => showErrorToast(err?.message || "Unable to load the rides."))
      .finally(() => setLoading(false));
  }, [loadTrips]);

  const search = async () => {
    setSearching(true);
    try {
      await loadTrips(filters);
    } catch (err) {
      showErrorToast(err?.message || "Unable to load the rides.");
    } finally {
      setSearching(false);
    }
  };

  const shown = useMemo(() => filterTrips(trips, view), [trips, view]);
  const count = useMemo(() => countTrips(filterTrips(trips, { ...view, tile: "" })), [trips, view]);
  // Fly / non fly of the rides the other filters show (not narrowed by the duty choice itself).
  const dutyCount = useMemo(() => countTrips(filterTrips(trips, { ...view, dutyTypeId: 0, tile: "" })), [trips, view]);
  // Clubbed rides among those the other filters show.
  const clubCount = useMemo(() => countClub(filterTrips(trips, { ...view, club: false })), [trips, view]);

  const openTrack = (ride) => {
    setTrack({ open: true, transactionId: ride.transactionId, airlineCode: ride.airlineCode, employeeNumber: ride.employeeNo, vehicleNo: ride.vehicleNo });
    resizeGrid();
  };
  const closeTrack = () => {
    setTrack(NO_TRACK);
    resizeGrid();
  };
  const openReplay = (ride) => {
    setReplay(ride);
    resizeGrid();
  };
  const openMobile = (ride) => {
    stashGridState({ page: "dispatch", filters, applied, airports, trips, view });
    history.push("/crew-activity-mobile", { employeeNumber: ride.employeeNo, sourceUrl: "/dispatch" });
  };
  const actions = { onTrack: openTrack, onReplay: openReplay, onMobile: openMobile };
  const range = `${moment(applied.from).format("DD/MM/YYYY")} – ${moment(applied.to).format("DD/MM/YYYY")}`;

  return (
    <MasterPage title="Crew Transfer">
      <div className="dsp-page">
        <DispatchFilterBar
          filters={filters}
          airports={airports}
          searching={searching}
          onChange={(c) => setFilters((p) => ({ ...p, ...c }))}
          onSearch={search}
        />

        {loading ? (
          <div className="dsp-panel">
            <DataGridSkeletonPageSimpleWithTitle />
          </div>
        ) : (
          <>
            <DispatchSummary
              count={count}
              tile={view.tile}
              onTile={(tile) => setView((v) => ({ ...v, tile }))}
              dutyCount={dutyCount}
              duty={view.dutyTypeId}
              onDuty={(dutyTypeId) => setView((v) => ({ ...v, dutyTypeId }))}
              clubCount={clubCount}
              club={view.club}
              onClub={(club) => setView((v) => ({ ...v, club }))}
              total={trips.length}
              allShown={!view.dutyTypeId && !view.timeSlotId && !view.club && !view.tile}
              onAll={() => setView(ALL_VIEW)}
            />
            <section className="dsp-panel" aria-label="Rides">
              <header className="dsp-panel__head">
                <h2 className="dsp-panel__title">
                  <i className="fas fa-clock" aria-hidden="true" /> Crew transfers <small>{range}</small>
                </h2>
                <div className="dsp-panel__filters">
                  <SelectBox
                    className="dsp-mini-select"
                    dataSource={DUTY_TYPES}
                    displayExpr="name"
                    valueExpr="id"
                    value={view.dutyTypeId}
                    inputAttr={{ "aria-label": "Duty type" }}
                    onValueChanged={(e) => setView((v) => ({ ...v, dutyTypeId: e.value ?? 0 }))}
                  />
                  <SelectBox
                    className="dsp-mini-select"
                    dataSource={TIME_SLOTS}
                    displayExpr="name"
                    valueExpr="id"
                    value={view.timeSlotId}
                    inputAttr={{ "aria-label": "Time slot" }}
                    onValueChanged={(e) => setView((v) => ({ ...v, timeSlotId: e.value ?? 0 }))}
                  />
                </div>
              </header>
              <ResponsiveRecords
                records={shown}
                keyOf={(r, i) => `${r.rideId}|${r.employeeNo}|${i}`}
                searchText={tripSearchText}
                searchPlaceholder="Search rides"
                emptyText="No ride matches these filters."
                renderCard={(r) => <DispatchCard ride={r} {...actions} />}
                renderGrid={() => <DispatchGrid rides={shown} gridRef={gridRef} {...actions} />}
              />
            </section>
          </>
        )}
      </div>

      {/* Live tracking / replay slide-in panels: unchanged (map design is not part of this page's redesign). */}
      {track.open && (
        <div className="dc-live-panel">
          <div className="dc-live-panel-header">
            <span>
              Live Trip Tracking
              {track.vehicleNo ? ` · ${track.vehicleNo}` : ""}
            </span>
            <button type="button" className="dc-live-panel-close" aria-label="Close" onClick={closeTrack}>
              <i className="fas fa-times" />
            </button>
          </div>
          <div className="dc-live-panel-body">
            <LiveTripTracking
              key={track.transactionId}
              airlineCode={track.airlineCode}
              transactionId={track.transactionId}
              employeeNumber={track.employeeNumber}
              height="100%"
              className="dc-live-panel-map"
              onDeparted={closeTrack}
            />
          </div>
        </div>
      )}
      {replay && (
        <div className="dc-live-panel dc-replay-panel">
          <TripReplayMap
            key={replay.rideId}
            ride={replay}
            onClose={() => {
              setReplay(null);
              resizeGrid();
            }}
            height="100%"
          />
        </div>
      )}
    </MasterPage>
  );
}

export default DispatchComponent;
